import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import { authAPI } from '../api';
import { getCookie } from '../utils/cookies';
import ThemeToggle from '../components/ThemeToggle';

import './Auth.css';

function Auth() {
  // Login only - no signup (clients get accounts via GHL payment)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    acceptedTerms: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/admin/dashboard';
  
  // Check for photographer token and redirect to photographer page
  useEffect(() => {
    console.log('🔍 Checking for photographer token...');
    
    // Check if launched from PWA (standalone mode)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true || 
                  searchParams.get('source') === 'pwa';
    
    console.log('Is PWA:', isPWA);
    
    // Check localStorage first (Android), then cookies (iOS PWA)
    const photographerToken = localStorage.getItem('photographerToken') || getCookie('photographerToken');
    console.log('Token found:', !!photographerToken);
    console.log('Token source:', localStorage.getItem('photographerToken') ? 'localStorage' : getCookie('photographerToken') ? 'cookie' : 'none');
    
    if (photographerToken) {
      try {
        const payload = JSON.parse(atob(photographerToken.split('.')[1]));
        console.log('Token payload:', payload);
        
        if (payload.eventId) {
          console.log(`➡️ Redirecting to /photographer/${payload.eventId}`);
          
          // Use navigate for React Router, but force reload if PWA to ensure clean state
          if (isPWA) {
            window.location.href = `/photographer/${payload.eventId}`;
          } else {
            navigate(`/photographer/${payload.eventId}`);
          }
        }
      } catch (error) {
        console.error('Invalid photographer token:', error);
        localStorage.removeItem('photographerToken'); // Clean up invalid token
      }
    } else if (isPWA) {
      // PWA launched but no photographer token - show error
      console.warn('⚠️ PWA launched without photographer token');
      setError('Please visit the photographer page in your browser first, then add to home screen.');
    }
  }, [navigate, searchParams]);

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login({ email: formData.email, password: formData.password });

      login(response.data.token, response.data.admin || response.data.venue);
      navigate(returnTo);
    } catch (err) {
      console.error('Login error:', err);
      if (err.response) {
        // Server responded with error
        setError(err.response?.data?.error || `Server error: ${err.response.status}`);
      } else if (err.request) {
        // Request made but no response
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        // Something else happened
        setError(err.message || 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="login-page">
      <ThemeToggle />
      <div className="login-card">
        <div className="login-header">
          <img 
            src={logoUrl} 
            alt="EventPic Live" 
            className="login-logo"
          />
          <h1>Login</h1>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter email"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter password"
              className="form-input"
            />
          </div>

          <button type="submit" className="btn-primary login-submit" disabled={loading}>
            {loading ? 'Please wait...' : 'Login'}
          </button>
        </form>

        <div className="login-footer">
          <Link to="/forgot-password" className="link-primary">
            Forgot Password?
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Auth;
